<template>
  <div class="device_preview pb-6 px-12">
    <v-skeleton-loader
      v-if="device"
      :loading="deviceDetailsLoading"
      type="list-item, divider, list-item-avatar, list-item-three-line@4, list-item-avatar-two-line@4"
      class="skeleton-loader--table"
    >
      <div
        class="d-flex align-center justify-space-between pb-4 pt-4 details-preview-header coronet-white-base"
      >
        <!--        TODO: ask if this part should be unified as well-->
        <template v-if="isMspContext">
          <div>
            <div
              class="body2 text-indigo-medium"
              data-testid="devices-preview-protected-since-date"
            >
              {{ $t("general.protectedSince") }}
              {{ getFormattedDateTime(deviceDetails.firstActivity, "ll") }}
            </div>
            <div
              v-if="errorsList.length > 0"
              class="body2 d-flex align-center text-red-dark mt-1"
              data-testid="device-preview-error-list"
            >
              <v-icon icon="$warning" class="mr-1" />
              <div v-for="(error, index) in errorsList" :key="error">
                <span>{{ $t(`devices.statuses.${error}`) }}</span>
                <span v-if="index < errorsList.length - 1">,&nbsp;</span>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <span
            v-if="deviceDetails.uninstalled"
            class="body2 text-red-dark"
            data-testid="device-preview-uninstalled-status"
          >
            {{ $t("devices.statuses.agentUninstalled") }}
            {{ getFormattedDateTime(deviceDetails.lastActivity, "ll") }}
          </span>
          <span
            v-else-if="showIsolationStatus"
            class="body2 text-red-dark"
            data-testid="device-preview-isolation-status"
          >
            {{ $t(`devices.statuses.${deviceDetails.isolationStatus}`) }}
            {{ getFormattedDateTime(deviceDetails.lastActivity, "ll") }}
          </span>
          <span
            v-else
            class="body2 text-indigo-medium"
            data-testid="devices-preview-protected-since-date"
          >
            {{ $t("general.protectedSince") }}
            {{ getFormattedDateTime(deviceDetails.firstActivity, "ll") }}
          </span>
        </template>
        <v-menu v-if="actionsList.length" offset-y location="bottom right">
          <template #activator="{ props }">
            <v-btn
              class="ml-8"
              rounded
              color="primary"
              data-testid="devices-preview-actions-btn"
              v-bind="props"
            >
              {{ $t("general.actions") }}
              <v-icon icon="$triangle" />
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="action in actionsList"
              :key="action"
              :disabled="isActionDisabled(action)"
              @click="onActionClick(action, deviceDetails)"
            >
              <v-tooltip v-if="showNoLicenseTooltip(action)" open-delay="300" location="top">
                <template #activator="{ props: tooltipProps }">
                  <v-list-item-title
                    v-bind="tooltipProps"
                    class="action-title"
                    @click.stop
                    :data-testid="`devices-page-action-${action}`"
                  >
                    {{ $t(`devices.actions.${action}`) }}
                  </v-list-item-title>
                </template>
                {{ $t("devices.noAvailableLicenses") }}
              </v-tooltip>
              <v-list-item-title v-else :data-testid="`devices-page-action-${action}`">
                {{ $t(`devices.actions.${action}`) }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <device-preview-info-section
        :is-mobile="isMobile"
        :action-not-allowed="actionNotAllowed"
        :device-details="deviceDetails"
        :context="context"
        @action-clicked="onActionClick"
        @merge-duplicates-clicked="mergeDuplicateDevices(deviceDetails.enrollmentCode)"
        @ignore-duplicates-clicked="ignoreDuplicateDevicesMerge(deviceDetails.enrollmentCode)"
        @scroll-to-activity-logs="scrollToActivityLogs"
      ></device-preview-info-section>
      <template v-if="!isMobile">
        <drives-list
          class="mb-5"
          :drives="deviceDetails.drives"
          :loading="deviceDrivesLoading"
          :enrollment-code="deviceDetails.enrollmentCode"
          :os-type="deviceDetails.osType"
          :device-details="deviceDetails"
        />
        <entity-tickets-list
          class="mb-5"
          :show-info-tooltip="restrictedTicketModules.length > 0"
          :info-tooltip-text="modulesTooltipText"
          :access-restricted="ticketsAccessRestricted"
          :tickets="deviceDetails.tickets"
          :show-resolve="!deviceDetails.offline"
          :all-open-tickets-link="allOpenTicketsLink"
          @view-vulnerability="viewVulnerability($event)"
        />
        <last-logged-in-users
          class="mb-5"
          :last-logged-in-users="deviceDetails.lastLoggedInUsers"
        />
      </template>
      <entity-activity-logs
        :hide-all-activity-link="isMspContext ? true : activityLogsAccessRestricted"
        :access-restricted="activityLogsAccessRestricted"
        :activity-logs="deviceDetails.activityLogs"
        :entity-id="deviceDetails.enrollmentCode"
        search-prefix="enrollmentCode"
        :loading="false"
        @undo-clicked="undoActivityLog(deviceDetails.enrollmentCode, $event)"
      />
    </v-skeleton-loader>
  </div>
</template>

<script setup lang="ts" generic="T extends PreviewDevice">
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { getFormattedDateTime } from "@/_helpers/date";
import { AppContext } from "@/types.ts";
import { DeviceAction, IsolationStatus, OsType } from "@/constants/devices.ts";
import { RouteName } from "@/constants/routes.ts";
import { TicketStatus, TicketTrigger } from "@/constants/tickets.ts";

import DevicePreviewInfoSection from "@/components/devices/device-preview/DevicePreviewInfoSection.vue";
import LastLoggedInUsers from "@/components/devices/LastLoggedInUsers.vue";
import DrivesList from "@/components/devices/DrivesList.vue";
import EntityTicketsList from "@/components/EntityTicketsList.vue";
import EntityActivityLogs from "@/components/EntityActivityLogs.vue";

import type { DeviceLabel } from "@/_store/devices-settings.module.ts";
import type {
  BaseDevicePreviewConfig,
  ConfigActionItem,
  ConfigDetails,
  PreviewDevice,
} from "./device-preview.types.ts";

const props = defineProps<{
  device: T | null;
  config: BaseDevicePreviewConfig<T, ConfigDetails<T>, ConfigActionItem<T>>;
}>();

const router = useRouter();
const context = props.config.context;
const isMspContext = computed(() => context === AppContext.MSP);
const dlpActionDisabled = computed(() => props.config.deviceSettingsDisabled.value);

const actionNotAllowed = props.config.actionNotAllowed;
const protectionLimit = props.config.protectionLimit;
const activityLogsAccessRestricted = props.config.activityLogsAccessRestricted;
const ticketsAccessRestricted = props.config.ticketsAccessRestricted;
const restrictedTicketModules = props.config.restrictedTicketModules;
const modulesTooltipText = props.config.modulesTooltipText;

const deviceDetailsLoading = computed(() => props.config.deviceDetailsLoading.value);
const deviceDrivesLoading = computed(() => props.config.deviceDrivesLoading.value);

const deviceDetails = computed(() => {
  if (props.config.context === AppContext.MSP) {
    return props.config.deviceDetails.value; // TS knows this is MspDeviceDetails
  }
  return props.config.deviceDetails.value; // TS knows this is DeviceDetails
});

const actionsList = ref<DeviceAction[]>([]);

const retrieveDeviceDetails = async (device: T) => {
  await props.config.getDeviceDetails(device);
  await props.config.fetchDeviceSettings(device);
  actionsList.value = await props.config.getDeviceActions(device);
};

watch(
  () => props.device,
  async (device: T | null) => {
    if (!device) {
      actionsList.value = [];
      return;
    }
    await retrieveDeviceDetails(device);
  },
  { immediate: true, deep: true }
);

async function onActionClick(
  action: DeviceAction,
  details: ConfigDetails<T>,
  payload?: DeviceLabel
) {
  await props.config.handleAction(action, details, payload);
}

const mergeDuplicateDevices = (enrollmentCode: string) =>
  props.config.mergeDuplicateDevices(enrollmentCode);

const ignoreDuplicateDevicesMerge = (enrollmentCode: string) =>
  props.config.ignoreDuplicateDevicesMerge(enrollmentCode);

const undoActivityLog = (enrollmentCode: string, activityLogId: string) =>
  props.config.undoActivityLog(enrollmentCode, activityLogId);

const scrollToActivityLogs = () => {
  document
    .querySelector('[data-testid="entity-activity-logs"]')
    ?.scrollIntoView({ behavior: "smooth" });
};

const showIsolationStatus = computed(() => {
  return (
    deviceDetails.value?.isolationStatus &&
    [IsolationStatus.ISOLATED].includes(deviceDetails.value.isolationStatus)
  );
});

const isMobile = computed(() => {
  return deviceDetails.value?.osType
    ? [OsType.IOS, OsType.ANDROID].includes(deviceDetails.value.osType)
    : false;
});

// This computed is now fully type-safe
const errorsList = computed(() => {
  if (props.config.context !== AppContext.MSP) {
    return [];
  }
  if (!deviceDetails.value) {
    return [];
  }

  const isolated = showIsolationStatus.value;
  const unresolvedTickets = deviceDetails.value.vulnerabilities.length > 0;

  return Object.entries({
    isolated,
    unresolvedTickets,
  })
    .filter(([, flag]) => flag)
    .map(([key]) => key);
});

const allOpenTicketsLink = computed(() => {
  const query = {
    search: deviceDetails.value?.enrollmentCode,
    eventTriggers: deviceDetails.value?.tickets.map((v) => v.name).join(","),
    status: TicketStatus.OPEN,
  };
  return {
    name: props.config.context === AppContext.MSP ? RouteName.MSP_TICKETS_PAGE : RouteName.TICKETS,
    query,
  };
});

function viewVulnerability(vulnerability: TicketTrigger) {
  router.push({
    name: props.config.context === AppContext.MSP ? RouteName.MSP_TICKETS_PAGE : RouteName.TICKETS,
    query: {
      eventTriggers: vulnerability,
      search: deviceDetails.value?.enrollmentCode,
    },
  });
}

const noLicensesRemaining = computed(() => protectionLimit.value?.remaining === 0);

const showNoLicenseTooltip = (action: DeviceAction) =>
  action === DeviceAction.LICENSE && noLicensesRemaining.value;

const isActionDisabled = (action: DeviceAction) => {
  switch (action) {
    case DeviceAction.DLP_PARTIAL_SCAN:
    case DeviceAction.STOP_DLP_SCAN:
      return dlpActionDisabled.value;
    case DeviceAction.UPDATE_AGENT_TO_LATEST:
    case DeviceAction.OPEN_REMOTE_SHELL_SESSION:
      return actionNotAllowed.value;
    case DeviceAction.LICENSE:
      return noLicensesRemaining.value;
    default:
      return false;
  }
};
</script>

<style scoped lang="scss">
.action-title {
  pointer-events: auto;
}
</style>
